import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";

import { Text, View } from "@/styles/reactnative";
import { ThemeContext } from "@/theme/ThemeProvider";
import { useBalance } from "@app/hooks/user/mybalance";
import { Input } from "antd";
import BigNumber from "bignumber.js";
import { useTranslation } from "react-i18next";
import { useFetcherPublic } from "../../hooks/fetcher";
import CModalHeader from "../Modal/CModalHeader";
import TransferAmount from "./TransferAmount";
import TransferCoin from "./TransferCoin";
import TransferFooter from "./TransferFooter";
import TransferFromTo from "./TransferFromTo";
import { truncateWithComma } from "@/utils/Truncate";

const TransferModal = ({ setShowModal, initialWallet = "spotwallet" }) => {
  const { drawerBgColor } = useContext(ThemeContext).theme.COLORS;
  const {  isDarkMode } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';
  const { t } = useTranslation();

  const [transferFrom, setTransferFrom] = useState(initialWallet);
  const [transferTo, setTransferTo] = useState(initialWallet === "spotwallet" ? "futurewallet" : 
                                              initialWallet === "futurewallet" ? "spotwallet" : 
                                              initialWallet === "rewardwallet" ? "futurewallet" : 
                                              "futurewallet");
  const [amount, setAmount] = useState("");
  // const { data: tradeStore } = useTradeStore()
  let coinType = "USDT";

  // transferFrom이 변경될 때 transferTo 자동 설정
  useEffect(() => {
    // 리워드지갑에서는 선물지갑으로만 전송 가능
    if (transferFrom === "rewardwallet") {
      setTransferTo("futurewallet");
    }
    // 선물지갑에서는 현물지갑으로 전송
    else if (transferFrom === "futurewallet" && transferTo === "rewardwallet") {
      setTransferTo("spotwallet");
    }
    // 현물지갑에서는 선물지갑으로 전송
    else if (transferFrom === "spotwallet" && 
             (transferTo === "spotwallet" || transferTo === "rewardwallet")) {
      setTransferTo("futurewallet");
    }
  }, [transferFrom]);

  // 지갑 유형에 따른 type 값 설정
  let type = 0;
  if (transferFrom === "futurewallet") {
    type = 1;
  } else if (transferFrom === "globalwallet") {
    coinType = "KRW";
    type = 9;
  } else if (transferFrom === "rewardwallet") {
    type = 7;
  }

  // 선택된 지갑 유형과 코인에 따라 잔액을 조회
  const { data: balance } = useBalance(coinType, type);

  const currencyList = useFetcherPublic("/system/currency");

  const equivusd = currencyList?.data?.result?.result?.find(
    (item: any) => item?.code === "KRW"
  );

  // 실제 표시할 잔액 계산
  const displayBalance = new BigNumber(balance?.balance || 0)
    .minus(balance?.lockbalance || 0)
    .toNumber();

  return (
    <ModernModalContainer isDarkMode={isDarkMode}>
      {/* <BackgroundGradient isDarkMode={isDarkMode} /> */}
      
      <ModalContent>
        <CModalHeader title={"transfer"} closeModal={() => setShowModal(false)} />
        
        <ContentWrapper>
          <SectionCard isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>
              {t("transferDirection")}
            </SectionTitle>
            <TransferFromTo
              transferFrom={transferFrom}
              setTransferFrom={setTransferFrom}
              transferTo={transferTo}
              setTransferTo={setTransferTo}
            />
          </SectionCard>

          <SectionCard isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>
              {t("coin")}
            </SectionTitle>
            <TransferCoin
              coinType={transferTo === "globalwallet" ? "KRW" : coinType}
            />
          </SectionCard>

          <SectionCard isDarkMode={isDarkMode}>
            <SectionTitle isDarkMode={isDarkMode}>
              {t("amount")}
            </SectionTitle>
            <TransferAmount
              balance={displayBalance}
              amount={amount}
              setAmount={setAmount}
              coinType={coinType}
            />
            
            <BalanceInfo isDarkMode={isDarkMode}>
              <BalanceLabel>{t("availableBalance")}</BalanceLabel>
              <BalanceValue>
                {truncateWithComma(displayBalance, 4)} USDT
              </BalanceValue>
            </BalanceInfo>
          </SectionCard>

          {(transferFrom === "globalwallet" || transferTo == "globalwallet") && (
            <SectionCard isDarkMode={isDarkMode}>
              <SectionTitle isDarkMode={isDarkMode}>
                {t("transferamount") + " : " + t("currencyrate")} ({equivusd?.price})
              </SectionTitle>
              <CurrencyConversionInput
                isDarkMode={isDarkMode}
                readOnly
                value={
                  transferFrom === "globalwallet"
                    ? BigNumber(amount || 0)
                        .dividedBy(equivusd?.price)
                        .toFormat(6) + " USDT"
                    : BigNumber(amount || 0)
                        .multipliedBy(equivusd?.price)
                        .toFormat(0) + " KRW"
                }
              />
            </SectionCard>
          )}

          <FooterWrapper>
            <TransferFooter
              transferFrom={
                transferFrom === "spotwallet"
                  ? 0
                  : transferFrom === "futurewallet"
                  ? 1
                  : transferFrom === "globalwallet"
                  ? 9
                  : transferFrom === "rewardwallet"
                  ? 7
                  : 0
              }
              transferTo={
                transferTo === "spotwallet"
                  ? 0
                  : transferTo === "futurewallet"
                  ? 1
                  : transferTo === "globalwallet"
                  ? 9
                  : transferTo === "rewardwallet"
                  ? 7
                  : 0
              }
              amount={amount}
              coinType={coinType}
              balance={displayBalance}
            />
          </FooterWrapper>
        </ContentWrapper>
      </ModalContent>
    </ModernModalContainer>
  );
};

// Styled Components
const ModernModalContainer = styled.div<{ isDarkMode: boolean }>`
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: ${props => props.isDarkMode 
    ? 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 50%, #0F0F0F 100%)'
    : 'linear-gradient(135deg, #2C2C2C 0%, #1A1A1A 50%, #0F0F0F 100%)'
  };
  box-shadow: ${props => props.isDarkMode
    ? '0 20px 40px rgba(0, 0, 0, 0.8), 0 15px 30px rgba(0, 0, 0, 0.6)'
    : '0 20px 40px rgba(0, 0, 0, 0.7), 0 15px 30px rgba(0, 0, 0, 0.5)'
  };
  border: 1px solid ${props => props.isDarkMode 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(255, 255, 255, 0.15)'
  };
  transition: all 0.3s ease;
`;

const BackgroundGradient = styled.div<{ isDarkMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 150px;
  background: ${props => props.isDarkMode
    ? 'linear-gradient(135deg, #FF5C17 0%, #FF8A50 50%, #7E39FF 100%)'
    : 'linear-gradient(135deg, #FF5C17 0%, #FF8A50 50%, #FFB084 100%)'
  };
  opacity: ${props => props.isDarkMode ? '0.15' : '0.08'};
  z-index: 0;
`;

const ModalContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 20px;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 10px;
`;

const SectionCard = styled.div<{ isDarkMode: boolean }>`
  background: ${props => props.isDarkMode
    ? 'linear-gradient(145deg, rgba(40, 40, 40, 0.8) 0%, rgba(30, 30, 30, 0.9) 100%)'
    : 'linear-gradient(145deg, rgba(60, 60, 60, 0.8) 0%, rgba(45, 45, 45, 0.9) 100%)'
  };
  border-radius: 12px;
  padding: 20px;
  border: 1px solid ${props => props.isDarkMode 
    ? 'rgba(255, 255, 255, 0.15)' 
    : 'rgba(255, 255, 255, 0.2)'
  };
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.isDarkMode
      ? '0 8px 25px rgba(0, 0, 0, 0.5)'
      : '0 8px 25px rgba(0, 0, 0, 0.4)'
    };
    border-color: ${props => props.isDarkMode 
      ? 'rgba(255, 92, 23, 0.4)' 
      : 'rgba(255, 92, 23, 0.5)'
    };
  }
`;

const SectionTitle = styled.h3<{ isDarkMode: boolean }>`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.isDarkMode ? '#FFFFFF' : '#2C3E50'};
  letter-spacing: 0.5px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 30px;
    height: 2px;
    background: linear-gradient(90deg, #FF5C17 0%, #FF8A50 100%);
    border-radius: 1px;
  }
`;

const BalanceInfo = styled.div<{ isDarkMode: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding: 12px 16px;
  background: ${props => props.isDarkMode
    ? 'linear-gradient(135deg, rgba(255, 92, 23, 0.1) 0%, rgba(255, 138, 80, 0.05) 100%)'
    : 'linear-gradient(135deg, rgba(255, 92, 23, 0.08) 0%, rgba(255, 138, 80, 0.04) 100%)'
  };
  border-radius: 10px;
  border: 1px solid ${props => props.isDarkMode 
    ? 'rgba(255, 92, 23, 0.2)' 
    : 'rgba(255, 92, 23, 0.15)'
  };
`;

const BalanceLabel = styled.span`
  font-size: 12px;
  color: #7F8C8D;
  font-weight: 500;
`;

const BalanceValue = styled.span`
  font-size: 14px;
  color: #FF5C17;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const CurrencyConversionInput = styled(Input)<{ isDarkMode: boolean }>`
  margin-top: 12px;
  background: ${props => props.isDarkMode
    ? 'rgba(40, 40, 40, 0.8)'
    : 'rgba(50, 50, 50, 0.8)'
  } !important;
  border: 1px solid ${props => props.isDarkMode 
    ? 'rgba(255, 255, 255, 0.15)' 
    : 'rgba(255, 255, 255, 0.2)'
  } !important;
  border-radius: 8px !important;
  padding: 12px 16px !important;
  font-weight: 600 !important;
  color: ${props => props.isDarkMode ? '#FFFFFF' : '#E0E0E0'} !important;
  font-size: 14px !important;
  
  &:focus, &:hover {
    border-color: #FF5C17 !important;
    box-shadow: 0 0 0 2px rgba(255, 92, 23, 0.2) !important;
  }
`;

const FooterWrapper = styled.div`
  margin-top: auto;
  padding-top: 20px;
`;

export default React.memo(TransferModal);
